import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactEcharts from 'echarts-for-react'

/**
 * @description Doughnut chart with the distribution of consumed tokens by AI model,
 * showing the total token count in the center of the doughnut.
 * @param {{ data: { models: Array<{name:string, tokens:number, color?:string}>, totalLabel?:string }, height?:string|number }} props
 */
const MODEL_COLOR_FALLBACK = ['#00A4E0', '#0B7FA9', '#69B75C', '#FACC15', '#9CA3AF']

function formatCompactNumber(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 'N/A'
  if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  return num.toString()
}

function ModelDistributionGraph(props) {
  const { data, height = '100%' } = props
  const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'globalBillingScreen' })

  const total = useMemo(() => {
    const models = data?.models
    if (!models || !models.length) return 0
    return models.reduce((acc, model) => acc + (Number(model.tokens) || 0), 0)
  }, [data])

  const option = useMemo(() => {
    const models = data?.models
    if (!models || !models.length) return

    return {
      tooltip: { trigger: 'item', confine: true },
      legend: { bottom: 0, icon: 'circle' },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          data: models.map((model, index) => ({
            value: Number(model.tokens) || 0,
            name: model.name,
            itemStyle: { color: model.color || MODEL_COLOR_FALLBACK[index % MODEL_COLOR_FALLBACK.length] }
          }))
        }
      ],
      graphic: {
        type: 'group',
        left: 'center',
        top: 'center',
        children: [
          {
            type: 'text',
            style: {
              text: formatCompactNumber(total),
              textAlign: 'center',
              fontSize: 20,
              fontWeight: 700,
              fill: '#000'
            },
            top: -10
          },
          {
            type: 'text',
            style: {
              text: data?.totalLabel || t('modelDistribution.totalLabel'),
              textAlign: 'center',
              fontSize: 12,
              fill: '#6b7280'
            },
            top: 14
          }
        ]
      }
    }
  }, [data, total, i18n.language, t])

  return (
    <div className="dashboardView-card dashboard-graph-card">
      <p className="dashboardView-card-title">{t('modelDistribution.title')}</p>
      {option ? <ReactEcharts option={option} style={{ height }} /> : <p>N/D</p>}
    </div>
  )
}

export default ModelDistributionGraph
